import React, { useMemo, useState } from 'react';
import { Avatar, Checkbox, Chip, Grid, IconButton, InputAdornment, Paper, Typography } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import SearchIcon from '@material-ui/icons/Search';
import { Button, PhoneField, TextField } from '@roc/ui';

export interface TitleContact {
  contactId?: number;
  fullName: string;
  phone?: string;
  email?: string;
}

const emptyContact: TitleContact = { fullName: '', phone: '', email: '' };

interface TitleContactSectionProps {
  contacts: TitleContact[];
  selectedContactIds: Set<number>;
  onToggle: (contactId: number) => void;
  onAddContact: (draft: TitleContact) => void | Promise<void>;
  onUpdateContact?: (updated: TitleContact) => void;
  disabled?: boolean;
  /** When true, the edit button is hidden for contacts that are already persisted (positive contactId). */
  disableEditForPersisted?: boolean;
}

export const TitleContactSection = ({
  contacts,
  selectedContactIds,
  onToggle,
  onAddContact,
  onUpdateContact,
  disabled,
  disableEditForPersisted,
}: TitleContactSectionProps) => {
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<TitleContact>(emptyContact);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<TitleContact>(emptyContact);
  const [searchTerm, setSearchTerm] = useState('');

  const setField = (field: keyof TitleContact, value: string) =>
    setDraft(prev => ({ ...prev, [field]: value }));

  const setEditField = (field: keyof TitleContact, value: string) =>
    setEditDraft(prev => ({ ...prev, [field]: value }));

  const cancel = () => {
    setShowForm(false);
    setDraft(emptyContact);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(emptyContact);
  };

  const save = async () => {
    if (!draft.email?.trim()) return;
    setSaving(true);
    await onAddContact(draft);
    setSaving(false);
    cancel();
  };

  const saveEdit = () => {
    if (!editDraft.email?.trim() || editingId == null) return;
    onUpdateContact?.({ ...editDraft, contactId: editingId });
    cancelEdit();
  };

  const startEdit = (contact: TitleContact) => {
    setEditingId(contact.contactId!);
    setEditDraft({ ...contact });
    setShowForm(false);
  };

  const selectedContacts = contacts.filter(
    c => c.contactId != null && selectedContactIds.has(c.contactId)
  );

  const filteredContacts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return contacts;
    return contacts.filter(c => (c.fullName || c.email || '').toLowerCase().includes(term));
  }, [contacts, searchTerm]);

  return (
    <Grid container spacing={2}>

      {/* Section header */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#000' }}>
          Contact information
        </Typography>
        <Typography variant="body1" color="textPrimary">
          Select which contacts should receive communications for this loan.
        </Typography>
      </Grid>

      {/* Selected contact chips */}
      {selectedContacts.length > 0 && (
        <Grid item xs={12}>
          <Grid container spacing={1}>
            {selectedContacts.map(c => (
              <Grid item key={c.contactId}>
                <Chip
                  avatar={
                    <Avatar style={{ width: 24, height: 24, fontSize: 12 }}>
                      {(c.fullName || c.email)?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  }
                  label={c.fullName || c.email}
                  onDelete={disabled ? undefined : () => onToggle(c.contactId!)}
                  size="small"
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}

      {/* Search filter */}
      <Grid item xs={12}>
        <TextField
          standaloneLabel
          variant="outlined"
          placeholder="Search contacts by name"
          fullWidth
          testId="contactSearch"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      {/* Checkbox list */}
      <Grid item xs={12}>
        <div style={{ maxHeight: 320, overflowY: 'auto', overflowX: 'hidden' }}>
          <Grid container spacing={2} style={{ margin: 0, width: '100%' }}>
            {filteredContacts.map(contact => (
              <Grid item xs={12} key={contact.contactId ?? contact.email} style={{ paddingLeft: 0, paddingRight: 8 }}>
                {editingId === contact.contactId ? (
                  <Paper variant="outlined" style={{ padding: 12 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          standaloneLabel
                          label="Contact name"
                          variant="outlined"
                          value={editDraft.fullName}
                          fullWidth
                          testId="editContactName"
                          onChange={e => setEditField('fullName', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <PhoneField
                          standaloneLabel
                          label="Phone number"
                          variant="outlined"
                          value={editDraft.phone as any}
                          fullWidth
                          testId="editContactPhone"
                          name="editContactPhone"
                          onChange={(_name, value) => setEditField('phone', value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          standaloneLabel
                          label="Email address *"
                          type="email"
                          variant="outlined"
                          value={editDraft.email}
                          fullWidth
                          testId="editContactEmail"
                          onChange={e => setEditField('email', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container spacing={1}>
                          <Grid item>
                            <Button
                              testId="saveEditContact"
                              color="primary"
                              variant="contained"
                              onClick={saveEdit}
                              disabled={!editDraft.email?.trim()}
                            >
                              Save
                            </Button>
                          </Grid>
                          <Grid item>
                            <Button testId="cancelEditContact" color="primary" variant="outlined" onClick={cancelEdit}>
                              Cancel
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                ) : (
                  <Paper variant="outlined" style={{ padding: '8px 12px' }}>
                    <Grid container alignItems="center" wrap="nowrap" spacing={1}>
                      <Grid item>
                        <Checkbox
                          checked={contact.contactId != null && selectedContactIds.has(contact.contactId)}
                          onChange={
                            !disabled && contact.contactId != null
                              ? () => onToggle(contact.contactId!)
                              : undefined
                          }
                          disabled={disabled || contact.contactId == null}
                          color="primary"
                        />
                      </Grid>
                      <Grid item>
                        <Avatar style={{ width: 32, height: 32, fontSize: 14 }}>
                          {(contact.fullName || contact.email)?.charAt(0)?.toUpperCase()}
                        </Avatar>
                      </Grid>
                      <Grid item style={{ flex: 1 }}>
                        {contact.fullName && (
                          <Typography variant="body2">{contact.fullName}</Typography>
                        )}
                        <Typography variant="caption" color="textSecondary">
                          {[contact.phone, contact.email].filter(Boolean).join(' · ')}
                        </Typography>
                      </Grid>
                      {!disabled && onUpdateContact && contact.contactId != null &&
                        !(disableEditForPersisted && contact.contactId > 0) && (
                        <Grid item>
                          <IconButton size="small" onClick={() => startEdit(contact)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                )}
              </Grid>
            ))}
          </Grid>
        </div>
      </Grid>

      {/* Inline add contact form */}
      {showForm && (
        <Grid item xs={12}>
          <Paper variant="outlined" style={{ padding: 12 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  standaloneLabel
                  label="Contact name"
                  variant="outlined"
                  value={draft.fullName}
                  fullWidth
                  testId="contactDraftName"
                  onChange={e => setField('fullName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PhoneField
                  standaloneLabel
                  label="Phone number"
                  variant="outlined"
                  value={draft.phone as any}
                  fullWidth
                  testId="contactDraftPhone"
                  name="contactDraftPhone"
                  onChange={(_name, value) => setField('phone', value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  standaloneLabel
                  label="Email address *"
                  type="email"
                  variant="outlined"
                  value={draft.email}
                  fullWidth
                  testId="contactDraftEmail"
                  onChange={e => setField('email', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  <Grid item>
                    <Button
                      testId="saveContact"
                      color="primary"
                      variant="contained"
                      onClick={save}
                      disabled={saving || !draft.email?.trim()}
                    >
                      Save
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button testId="cancelContact" color="primary" variant="outlined" onClick={cancel}>
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      )}

      {!showForm && !disabled && (
        <Grid item xs={12}>
          <Button testId="addContact" color="primary" variant="outlined" onClick={() => { cancelEdit(); setShowForm(true); }}>
            + Add contact
          </Button>
        </Grid>
      )}

    </Grid>
  );
};
